import React, { useState } from 'react';
import {
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import { DESIGN_TOKENS } from '../../theme/designTokens';

const StyledAppBar = styled('header')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
  background: 'rgba(254, 254, 254, 0.9)',
  backgroundColor: 'rgba(254, 254, 254, 0.9)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(229, 231, 235, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Safari fallback - solid background when backdrop-filter isn't supported
  '@supports not (backdrop-filter: blur(20px))': {
    backgroundColor: '#FEFEFE',
    background: '#FEFEFE',
  },
}));

const Logo = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  
  '&:hover': {
    transform: 'translateY(-1px) scale(1.02)',
  },
  
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  mixBlendMode: 'multiply',
  
  [theme.breakpoints.down('sm')]: {
    height: '36px',
  },
  
  '&:hover': {
    filter: 'none',
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Montserrat", sans-serif !important',
  fontWeight: 200,
  fontSize: '1.5rem',
  color: '#111827',
  letterSpacing: '-0.025em',
  whiteSpace: 'nowrap',
  
  '& strong': {
    fontWeight: 600,
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: '#6B7280',
  fontSize: '0.9rem',
  fontWeight: 500,
  padding: theme.spacing(1, 1.5),
  borderRadius: DESIGN_TOKENS.borderRadius.sm,
  textTransform: 'none',
  textAlign: 'center',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  
  [theme.breakpoints.up('lg')]: {
    fontSize: '0.95rem',
    padding: theme.spacing(1, 2),
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: '50%',
    width: 0,
    height: 2,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    borderRadius: '1px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: 'translateX(-50%)',
  },
  
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(67, 56, 202, 0.04)',
    transform: 'translateY(-1px)',
    
    '&::before': {
      width: '80%',
    },
  },
  
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    
    '&::before': {
      width: '80%',
    },
  },
}));

const PrimaryNavButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  fontSize: '0.95rem',
  padding: theme.spacing(1.25, 2.5),
  borderRadius: DESIGN_TOKENS.borderRadius.sm,
  textTransform: 'none',
  boxShadow: DESIGN_TOKENS.shadows.button.default,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s',
  },
  
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
    transform: 'translateY(-1px)',
    boxShadow: DESIGN_TOKENS.shadows.button.hover,
    
    '&::before': {
      left: '100%',
    },
  },
  
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1),
  borderRadius: DESIGN_TOKENS.borderRadius.sm,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(67, 56, 202, 0.08)',
    transform: 'scale(1.05)',
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '280px',
    background: 'rgba(254, 254, 254, 0.98)',
    backgroundColor: 'rgba(254, 254, 254, 0.98)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: 'none',
    
    // Safari fallback - solid background when backdrop-filter isn't supported
    '@supports not (backdrop-filter: blur(20px))': {
      backgroundColor: '#FEFEFE',
      background: '#FEFEFE',
    },
  },
}));

const MobileNavButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '1rem',
  fontWeight: 500,
  padding: theme.spacing(1.5, 2),
  borderRadius: DESIGN_TOKENS.borderRadius.sm,
  textTransform: 'none',
  width: '100%',
  justifyContent: 'flex-start',
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(67, 56, 202, 0.08)',
    transform: 'translateX(4px)',
  },
  
  '&.active': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(67, 56, 202, 0.08)',
    fontWeight: 600,
  },
}));

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const { t } = useTranslation();

  const navItems = [
    { text: t('header.howItWorks'), href: '/how-it-works' },
    { text: t('header.reportContents'), href: '/report-contents' },
    { text: t('header.examples'), href: '/examples' },
    { text: t('header.guide'), href: '/gemba-guide' },
    { text: t('header.pricing'), href: '/pricing' },
    { text: t('header.privacy'), href: '/privacy' },
    { text: t('header.about'), href: '/about' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <StyledAppBar role="banner">
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: { xs: 1, sm: 1.5 }, px: { xs: 0 } }}>
            <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Logo
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <LogoImage
                  src="/images/GembaScan-logo.jpeg"
                  alt="Gemba Scan logo"
                />
                <LogoText>
                  <strong>Gemba</strong> Scan
                </LogoText>
              </Logo>
            </RouterLink>

            {!isMobile && (
              <Box component="nav" role="navigation" aria-label="Main navigation" sx={{ display: 'flex', alignItems: 'center', gap: { md: 0.5, lg: 1 } }}>
                {navItems.map((item) => (
                  <motion.div
                    key={item.href}
                    whileHover={{ y: -1 }}
                    whileTap={{ y: 0 }}
                  >
                    <RouterLink to={item.href} style={{ textDecoration: 'none' }}>
                      <NavButton
                        className={isActive(item.href) ? 'active' : ''}
                      >
                        {item.text}
                      </NavButton>
                    </RouterLink>
                  </motion.div>
                ))}
                
                <LanguageSwitcher />
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RouterLink to="/upload" style={{ textDecoration: 'none' }}>
                    <PrimaryNavButton sx={{ ml: { md: 1, lg: 2 } }}>
                      {t('header.upload')}
                    </PrimaryNavButton>
                  </RouterLink>
                </motion.div>
              </Box>
            )}

            {isMobile && (
              <MobileMenuButton
                onClick={handleMobileMenuToggle}
                aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              >
                <AnimatePresence mode="wait">
                  {mobileMenuOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CloseIcon />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <MenuIcon />
                    </motion.div>
                  )}
                </AnimatePresence>
              </MobileMenuButton>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>

      <MobileDrawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
      >
        <Box component="nav" role="navigation" aria-label="Mobile navigation" sx={{ padding: 3, paddingTop: 4 }}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, staggerChildren: 0.1 }}
          >
            {navItems.map((item, index) => (
              <motion.div
                key={item.href}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <RouterLink to={item.href} style={{ textDecoration: 'none' }}>
                  <MobileNavButton
                    onClick={handleMobileMenuToggle}
                    className={isActive(item.href) ? 'active' : ''}
                  >
                    {item.text}
                  </MobileNavButton>
                </RouterLink>
              </motion.div>
            ))}
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: navItems.length * 0.1 }}
            >
              <Box sx={{ mb: 2 }}>
                <LanguageSwitcher />
              </Box>
              
              <RouterLink to="/upload" style={{ textDecoration: 'none' }}>
                <PrimaryNavButton
                  onClick={handleMobileMenuToggle}
                  sx={{ width: '100%' }}
                >
                  {t('header.upload')}
                </PrimaryNavButton>
              </RouterLink>
            </motion.div>
          </motion.div>
        </Box>
      </MobileDrawer>
    </>
  );
};

export default Header; 